import axios from 'axios'
import { attach, type EffectParams } from 'effector'
import UAParser from 'ua-parser-js'
import { z } from 'zod'

import { appParams } from '@portal/client-info'
import { logger } from '@portal/logger'
import type { GameConnectConfig, PortalProfile } from '@portal/portal-api'

import * as chat from '@/entities/chat'
import {
    CrashInfoSchema,
    clientCrashed,
    clientCrashedV2,
    SendCrashlogParamsSchema,
    SendCrashlogV2ParamsSchema,
} from '@/entities/crashlog'
import { $gameConnectConfig, type GameClient, gameClientReady, gameConnectFx, preloaderReady } from '@/entities/game'
import * as portalModel from '@/entities/profile/portal'
import { $portalProfile, gameLevelUp, portalProfileAPI } from '@/entities/profile/portal'
import { getPublicProfileListFx, PublicProfileInfoListParamsSchema } from '@/entities/profile/public'
import { AccountNotProtectedDialog, ProtectAccountDialog } from '@/features/account-protection'
import { giveConsentRequested } from '@/features/email-notification-consent/model'
import { resolvePaymentProvider } from '@/features/payment/provider'
import { ShowPortalPaymentDialogParamsSchema } from '@/features/payment/types'
import { SupportDialog } from '@/features/support-ticket'
import { competitionAPI } from '@/shared/api'
import {
    Competition3PositionRequestSchema,
    Competition3TopRequestSchema,
    Leaderboard3TopRequestSchema,
} from '@/shared/api/competition/types'
import { analytics } from '@/shared/lib/analytics'
import { selectFile } from '@/shared/lib/file'
import { $i18n, $lang } from '@/shared/lib/i18n'
import { convertImage } from '@/shared/lib/image'

import type { GameWrapper } from './interface'
import {
    GameEventSchema,
    LevelUpParamsSchema,
    PurchaseParamsSchema,
    StoryStepParamsSchema,
    UpdateAvatarParamsSchema,
    UpdateNickParamsSchema,
} from './types'

export function initWrapper(): void {
    const wrapper = newGameWrapper()

    if (typeof window !== 'undefined') {
        window.taonga = wrapper
    }
}

/* eslint-disable effector/no-getState */
const newGameWrapper = (): GameWrapper => ({
    getBrowserInfo(): UAParser.IBrowser {
        return new UAParser().getBrowser()
    },

    getDeviceInfo(): UAParser.IDevice {
        return new UAParser().getDevice()
    },

    getGameLanguage(): string {
        return $lang.getState()
    },

    getPaymentCurrency: () => portalModel.$paymentCurrency.getState(),

    getPortalProfile(): PortalProfile {
        return $portalProfile.getState()!
    },

    async getPublicProfileInfoList(params) {
        validateParams(params, PublicProfileInfoListParamsSchema)

        return await getPublicProfileListFx(params)
    },

    async refreshPortalProfile(): Promise<PortalProfile> {
        return await portalProfileAPI.me()
    },

    getGameConnectConfig(): GameConnectConfig | null {
        return $gameConnectConfig.getState()
    },

    getRewardLinkRef(): string | null {
        return appParams['ref'] ?? null
    },

    getAppParams(): Record<string, string> {
        return appParams
    },

    async getCompetition3Top(params) {
        validateParams(params, Competition3TopRequestSchema)

        return await competitionAPI.competition3Top(params)
    },

    async getCompetition3Position(params) {
        validateParams(params, Competition3PositionRequestSchema)

        return await competitionAPI.competition3Position(params)
    },

    async getLeaderboard3Top(params) {
        validateParams(params, Leaderboard3TopRequestSchema)

        return await competitionAPI.leaderboard3Top(params)
    },

    async selectAvatar() {
        const imageFile = await selectFile({ accept: 'image/*' })
        if (!imageFile) {
            return null
        }

        const avatar = await convertImage(imageFile, {
            size: 150,
            format: 'image/png',
        })

        return avatar
    },

    async updateAvatar(params) {
        validateParams(params, UpdateAvatarParamsSchema)

        if ('avatarId' in params) {
            await portalProfileAPI.avatarSet({ avatar_id: params.avatarId })
        } else {
            await portalProfileAPI.imageSet({ image: params.avatarBlob })
        }
    },

    async updateNick(params) {
        validateParams(params, UpdateNickParamsSchema)

        await portalProfileAPI.nickSet({ nick: params.nick })
    },

    sendCrashlog(params): void {
        validateParams(params, SendCrashlogParamsSchema)

        clientCrashed(params)
    },

    sendCrashlogV2(params): void {
        validateParams(params, SendCrashlogV2ParamsSchema)

        // Contract drift must be visible without preventing crash reporting.
        const crashInfoResult = CrashInfoSchema.safeParse(params.crashInfo)
        if (!crashInfoResult.success) {
            logger.error({ issues: crashInfoResult.error.issues }, 'GameWrapper.sendCrashlogV2: unexpected crashInfo')
        }

        clientCrashedV2(params)
    },

    showSupportDialog() {
        showSupportDialogFx()
    },

    showPortalChat: () => chat.chatFocused(),

    async showPortalPaymentDialog(params: unknown): Promise<boolean> {
        const profile = $portalProfile.getState()
        const provider = resolvePaymentProvider(profile)

        const result = ShowPortalPaymentDialogParamsSchema.parse(params)

        if (provider === 'adyen') {
            const { AdyenPaymentDialog } = await import('@/features/payment-adyen')

            return new Promise<boolean>((resolve) => {
                AdyenPaymentDialog.model.showFx({
                    ...result,
                    onResult: resolve,
                })
            })
        }

        if (provider === 'coda') {
            const { CodaPaymentDialog } = await import('@/features/payment-coda')

            return new Promise<boolean>((resolve) => {
                CodaPaymentDialog.model.showFx({
                    ...result,
                    onResult: resolve,
                })
            })
        }

        const { XsollaPaymentDialog } = await import('@/features/payment-xsolla')

        return new Promise<boolean>((resolve) => {
            XsollaPaymentDialog.model.showFx({
                ...result,
                onResult: resolve,
            })
        })
    },

    showPortalAddEmailDialog() {
        showAddEmailDialogFx({
            titleIconClass: 'fa-solid fa-feather-pointed',
            headerBackgroundColor: 'info',
        })
    },

    showPortalVerifyEmailDialog() {
        showVerifyEmailDialogFx({
            titleIconClass: 'fa-solid fa-shield-check',
            headerBackgroundColor: 'info',
        })
    },

    giveEmailNotificationConsent() {
        giveConsentRequested({ source: 'in-game form' })
    },

    onGameEvent(params) {
        validateParams(params, GameEventSchema)

        logger.debug('GameWrapper.onGameEvent', params.eventName, params.payload)

        const { eventName, payload } = params

        if (eventName === 'level_updated') {
            analytics.levelup({ level: payload.level })
            gameLevelUp({ level: payload.level })

            // TODO завести в analytics провайдер Pushwoosh и передавать туда событие
        }

        if (eventName === 'payment_completed') {
            analytics.purchase({
                transactionId: payload.transactionId,
                grossEUR: payload.grossEUR,
                grossUSD: payload.grossUSD,
                firstPurchase: payload.firstPurchase,
            })
        }
    },

    async requestBrowserNotificationPermission(): Promise<void> {
        const { subscribeRequested } = await import('@/features/browser-notification')
        subscribeRequested()
    },

    async getBrowserNotificationSubscriptionStatus(): Promise<boolean> {
        const { checkSubscriptionFx } = await import('@/features/browser-notification')
        return await checkSubscriptionFx()
    },

    onPreloaderReady() {
        preloaderReady()
    },

    onGameReady(gameClient: GameClient) {
        gameClientReady(gameClient)
    },

    onGameLevelUp(params) {
        validateParams(params, LevelUpParamsSchema)

        analytics.levelup({ level: params.level })
        gameLevelUp({ level: params.level })
    },

    onGamePurchase(params) {
        validateParams(params, PurchaseParamsSchema)

        analytics.purchase({
            transactionId: params.transactionId,
            grossEUR: params.grossEUR,
            grossUSD: params.grossUSD,
            firstPurchase: params.firstPurchase,
        })
    },

    onGameStoryStep(params) {
        validateParams(params, StoryStepParamsSchema)

        analytics.storyStep({ label: params.label })
    },

    async reconnect(): Promise<GameConnectConfig> {
        return await gameConnectFx({ phase: 'reconnect' })
    },

    async ping(): Promise<void> {
        await axios.head(window.location.origin)
    },

    reload(): void {
        window.location.reload()
    },
})

function validateParams<T>(params: T, schema: z.ZodSchema<T>): T {
    try {
        return schema.parse(params)
    } catch (e) {
        if (e instanceof z.ZodError) {
            throw new Error(`Invalid params: ${JSON.stringify(e.flatten().fieldErrors)}`, { cause: e })
        }
        throw e
    }
}

const showSupportDialogFx = attach({
    source: portalModel.$portalProfile,
    mapParams: (_: void, profile) => ({
        defaultNick: profile?.nick,
        defaultEmail: profile?.email,
    }),
    effect: SupportDialog.model.showFx,
})

type ShowProtectAccountDialogParams = EffectParams<typeof ProtectAccountDialog.model.showFx>

const showAddEmailDialogFx = attach({
    source: $i18n,
    mapParams: (params: Omit<ShowProtectAccountDialogParams, 'intro' | 'title'>, data) => ({
        ...params,
        title: data.i18n.PORTAL_PRIZE_EMAIL_BIND__TITLE,
        intro: data.i18n.PORTAL_PRIZE_EMAIL_BIND__INTRO,
    }),
    effect: ProtectAccountDialog.model.showFx,
})

type ShowAccountNotProtectedDialogParams = EffectParams<typeof AccountNotProtectedDialog.model.showFx>

const showVerifyEmailDialogFx = attach({
    effect: AccountNotProtectedDialog.model.showFx,
    source: {
        portalProfile: portalModel.$portalProfile,
        i18n: $i18n,
    },
    mapParams: (
        params: Omit<ShowAccountNotProtectedDialogParams, 'intro' | 'email' | 'title'>,
        { portalProfile, i18n }
    ) => ({
        ...params,
        title: i18n.i18n.PORTAL_PRIZE_EMAIL_VERIFY__TITLE,
        intro: i18n.i18n.PORTAL_PRIZE_EMAIL_VERIFY__INTRO,
        email: portalProfile!.email,
    }),
})
